import { LogoutOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Col, Grid, Layout, Row, Space, Typography } from 'antd';
import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SellersDropdown from '../../components/sellers/SellersDropdown';
import UsersDropdown from '../../components/users/common/UsersDropdown';
import PosProductGrid from '../../components/pos/PosProductGrid';
import PosCart from '../../components/pos/PosCart';
import { BLUE4, WHITE } from '../../constants/color';
import { FIELD_CODE, SHIPPING_CODE } from '../../constants/orderConstants';
import { POS_WALK_IN_USER_ID } from '../../constants/generalConstants';
import { dashboardRoute } from '../../constants/pathname';
import getDashboardStyle from '../../utils/getDashboardStyle';
import {
  OrderEnum,
  PaymentMethod,
  ShippingData,
  UserListData,
} from '../../types';
import { alertMessage } from '../../utils/alertMessage';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../utils/axiosRequest';

const { Header, Content } = Layout;

// Address fields carried in formData; reset together when the customer changes
// so a customer with no saved address doesn't inherit the previous one.
const EMPTY_ADDRESS = {
  addressId: undefined,
  consignee: '',
  mobile: '',
  email: '',
  tel: '',
  address: '',
  postalCode: '',
  unitNumber: '',
  buzzerCode: '',
  addressType: '',
  latitude: 0,
  longitude: 0,
};

const initialFormData = () => ({
  sellerId: 0,
  userId: POS_WALK_IN_USER_ID,
  orderGoodsList: [] as any[],
  orderGoodsListTotal: '0.00',
  shippingTransportId: 0,
  formValue: [] as any[],
  ...EMPTY_ADDRESS,
});

/**
 * POS terminal: full-screen two-pane cashier UI.
 *
 * Holds a single `formData` state shaped like the order wizard's so the same
 * order/* endpoints and helpers can be reused. Left pane = quick product
 * selection, right pane = cart + checkout. Customer defaults to the preset
 * walk-in account (POS_WALK_IN_USER_ID); a registered member can be selected
 * instead. The selected customer's default saved address is auto-loaded.
 */
const PosTerminal = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const screens = Grid.useBreakpoint();

  const [formData, setFormData] = useState<{ [key: string]: any }>(
    initialFormData()
  );
  const [enums, setEnums] = useState<OrderEnum>();
  const [shippingList, setShippingList] = useState<ShippingData[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserListData>();
  const [orderAmount, setOrderAmount] = useState<{ [key: string]: any }>({});
  const [addressLoaded, setAddressLoaded] = useState(false);

  const sellerId = formData.sellerId;
  const userId = formData.userId;

  // --- Seller-scoped config: enums, shipping (auto-pick pickup), payment, form fields ---
  useEffect(() => {
    let active = true;

    getDataWithAuthToken('order/enum_list', { params: { sellerId } })
      .then((res) => {
        if (active && res && res.goodStatus) setEnums(res.data);
      })
      .catch((err) => console.log(err));

    getDataWithAuthToken('shipping/list')
      .then((res) => {
        if (active && res && res.goodStatus) {
          const list: ShippingData[] = res.data.list;
          setShippingList(list);
          // Prefer a pickup ("cac") method so a counter sale needs no shipping choice
          const pickup =
            list.find((s) => s.shippingCode === SHIPPING_CODE['cac']) ||
            list[0];
          if (pickup) {
            const isPickup = pickup.shippingCode !== SHIPPING_CODE['flat'];
            setFormData((prev) => ({
              ...prev,
              shippingId: pickup.shippingId,
              shippingCode: pickup.shippingCode,
              shippingName: pickup.shippingName,
              // Pickup defaults to today
              expectShippingTime:
                isPickup && !prev.expectShippingTime
                  ? moment()
                  : prev.expectShippingTime,
            }));
          }
        }
      })
      .catch((err) => console.log(err));

    getDataWithAuthToken('payment/enabled_list', {
      params: { isSeller: !!sellerId, sellerId },
    })
      .then((res) => {
        if (active && res && res.goodStatus) {
          const list: PaymentMethod[] = res.data.list;
          setPaymentMethods(list);
          if (list.length) {
            setFormData((prev) =>
              prev.payId
                ? prev
                : { ...prev, payId: list[0].payId, payName: list[0].payName }
            );
          }
        }
      })
      .catch((err) => console.log(err));

    getDataWithAuthToken('form_field/blank_list', {
      params: { sellerId, fieldCode: FIELD_CODE['ORDER'] },
    })
      .then((res) => {
        if (active && res && res.goodStatus) {
          setFormData((prev) => ({ ...prev, formValue: res.data.list }));
        }
      })
      .catch((err) => console.log(err));

    return () => {
      active = false;
    };
  }, [sellerId]);

  // --- Customer detail + default address, refetched on every customer change ---
  useEffect(() => {
    let active = true;
    setAddressLoaded(false);
    // Clear the previous customer's address and any balance they'd applied, so a
    // customer with no saved address starts blank and must be filled in.
    setFormData((prev) => ({ ...prev, ...EMPTY_ADDRESS, surplusAmount: 0 }));
    if (!userId) return;

    // Full detail (name, nickname, mobile, balance) — works for the walk-in
    // account too, which is never picked through the dropdown.
    getDataWithAuthToken('users/detail', { params: { userId } })
      .then((res) => {
        if (active && res && res.goodStatus) setSelectedUser(res.data);
      })
      .catch((err) => console.log(err));

    getDataWithAuthToken('users/user_address/list', { params: { userId } })
      .then((res) => {
        if (!active) return;
        if (res && res.goodStatus) {
          const address = res.data.list && res.data.list[0];
          if (address) {
            setFormData((prev) => ({
              ...prev,
              ...address,
              addressType: address.addressType
                ? address.addressType.code
                : '',
              latitude: Number(address.latitude),
              longitude: Number(address.longitude),
            }));
          }
          setAddressLoaded(true);
        } else {
          alertMessage('error', res?.msg || t('general.noResponse'));
        }
      })
      .catch((err) => console.log(err));
    return () => {
      active = false;
    };
  }, [userId, t]);

  // Recalculate goods subtotal after qty/remove edits (shared with the cart)
  const recalcGoods = useCallback(
    (orderGoodsList: any[]) => {
      if (!orderGoodsList.length) {
        setFormData((prev) => ({
          ...prev,
          orderGoodsList: [],
          orderGoodsListTotal: '0.00',
          shippingTransportId: 0,
        }));
        return;
      }
      postDataWithAuthToken('order/preview_goods', {
        userId,
        sellerId,
        orderGoodsList: orderGoodsList.map((good: any) => ({
          goodsId: good.goodsId,
          goodsName: good.goodsName,
          goodsUnit: good.goodsUnit,
          goodsNumber: good.goodsNumber,
          goodsSkuAttrIds:
            (good.goodsAttrId && good.goodsAttrId.split(',')) || undefined,
          goodsPrice: good.goodsPrice,
          mainGoodsPrice: good.mainGoodsPrice,
          goodsWeight: good.goodsWeight,
        })),
      })
        .then((res) => {
          if (res && res.goodStatus) {
            setFormData((prev) => ({
              ...prev,
              orderGoodsList: res.data.orderGoodsList,
              orderGoodsListTotal: res.data.goodsAmount,
              shippingTransportId: res.data.transportId,
            }));
          } else {
            alertMessage(
              'error',
              res?.msg || t('general.noResponse'),
              res?.data || undefined
            );
          }
        })
        .catch((err) => console.log(err));
    },
    [userId, sellerId, t]
  );

  // Recalculate full order fees / total whenever the cart or fulfillment changes
  useEffect(() => {
    if (!formData.orderGoodsList || !formData.orderGoodsList.length) {
      setOrderAmount({});
      return;
    }
    let active = true;
    postDataWithAuthToken('order/preview', {
      shippingId: formData.shippingId,
      shippingCode: formData.shippingCode,
      payId: formData.payId,
      postalCode: formData.postalCode,
      surplusAmount: formData.surplusAmount || undefined,
      sellerId,
      userId,
      orderGoodsList: formData.orderGoodsList.map((good: any) => ({
        goodsId: good.goodsId,
        goodsNumber: good.goodsNumber,
        goodsName: good.goodsName,
        goodsPrice: good.goodsPrice,
        goodsSkuAttrIds:
          (good.goodsAttrId && good.goodsAttrId.split(',')) || undefined,
        mainGoodsPrice: good.mainGoodsPrice,
        goodsWeight: good.goodsWeight || undefined,
      })),
    })
      .then((res) => {
        if (active && res && res.goodStatus) {
          setOrderAmount(res.data.orderAmount || {});
        }
      })
      .catch((err) => console.log(err));
    return () => {
      active = false;
    };
  }, [
    formData.orderGoodsList,
    formData.shippingId,
    formData.shippingCode,
    formData.payId,
    formData.postalCode,
    formData.surplusAmount,
    sellerId,
    userId,
  ]);

  const resetSale = () => {
    // Don't clear selectedUser here: the customer-detail effect only runs when
    // userId changes, so if we reset while already on the walk-in account it
    // would never repopulate. It's refreshed by the effect (member -> walk-in)
    // or stays correct when already on walk-in.
    setOrderAmount({});
    setFormData((prev) => ({
      ...initialFormData(),
      // keep the chosen store and its already-loaded fulfillment config
      sellerId: prev.sellerId,
      shippingId: prev.shippingId,
      shippingCode: prev.shippingCode,
      shippingName: prev.shippingName,
      payId: prev.payId,
      payName: prev.payName,
      formValue: prev.formValue,
    }));
  };

  const onSelectWalkIn = () => {
    // Same reasoning as resetSale: leave selectedUser to the userId effect so
    // pressing "walk-in" while already on it doesn't wipe the customer info.
    setFormData((prev) => ({ ...prev, userId: POS_WALK_IN_USER_ID }));
  };

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Header
        style={{
          backgroundColor: BLUE4,
          padding: '0 16px',
          height: 64,
          lineHeight: '64px',
        }}
      >
        <Row align="middle" justify="space-between" wrap={false} gutter={16}>
          <Col flex="none">
            <Space>
              <img src={getDashboardStyle().icon} width={28} alt="logo" />
              <Typography.Text strong style={{ color: WHITE, fontSize: 18 }}>
                {t('pos.title')}
              </Typography.Text>
            </Space>
          </Col>
          <Col flex="auto">
            <Row justify="end" gutter={8} wrap={false} align="middle">
              <Col flex="none">
                <ShopOutlined style={{ color: WHITE }} />
              </Col>
              <Col style={{ width: 200 }}>
                <SellersDropdown
                  onChange={(value, option: any) => {
                    const seller = option && option.seller;
                    setFormData((prev) => ({
                      ...prev,
                      sellerId: value || 0,
                      orderGoodsList: [],
                      orderGoodsListTotal: '0.00',
                      payId: undefined,
                      payName: undefined,
                      isRegionSale: seller ? seller.isRegionSale : undefined,
                    }));
                    setPaymentMethods([]);
                  }}
                />
              </Col>
              <Col flex="none">
                <UserOutlined style={{ color: WHITE }} />
              </Col>
              <Col style={{ width: 300 }}>
                <UsersDropdown
                  key={userId}
                  // Walk-in keeps the search box empty; a picked member seeds it
                  initialUser={
                    userId !== POS_WALK_IN_USER_ID && selectedUser
                      ? {
                          userId: selectedUser.userId,
                          userName: selectedUser.userName,
                          nickName: selectedUser.nickName,
                          mobilePhone: selectedUser.mobilePhone,
                        }
                      : undefined
                  }
                  onChange={(value, option: any) => {
                    if (value && option && option.user) {
                      setSelectedUser(option.user);
                      setFormData((prev) => ({ ...prev, userId: value }));
                    } else {
                      onSelectWalkIn();
                    }
                  }}
                />
              </Col>
              <Col flex="none">
                <Button
                  type={
                    userId === POS_WALK_IN_USER_ID ? 'primary' : 'default'
                  }
                  onClick={onSelectWalkIn}
                >
                  {t('pos.walkInCustomer')}
                </Button>
              </Col>
              <Col flex="none">
                <Button
                  danger
                  icon={<LogoutOutlined />}
                  onClick={() => history.push(dashboardRoute.home)}
                >
                  {screens.md ? t('pos.exit') : ''}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Header>
      <Content style={{ height: 'calc(100vh - 64px)' }}>
        <Row style={{ height: '100%' }}>
          <Col
            xs={24}
            md={15}
            lg={16}
            style={{ height: '100%', overflow: 'hidden' }}
          >
            <PosProductGrid
              sellerId={sellerId}
              userId={userId}
              formData={formData}
              setFormData={setFormData}
            />
          </Col>
          <Col
            xs={24}
            md={9}
            lg={8}
            style={{
              height: '100%',
              borderLeft: '1px solid #f0f0f0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <PosCart
              formData={formData}
              setFormData={setFormData}
              enums={enums}
              shippingList={shippingList}
              paymentMethods={paymentMethods}
              selectedUser={selectedUser}
              orderAmount={orderAmount}
              addressLoaded={addressLoaded}
              recalcGoods={recalcGoods}
              onSubmitted={resetSale}
            />
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default PosTerminal;
